import React from "react";
import { Box, Stack } from "@mantine/core";
import Logo from "../common/Logo/Logo";
import { useOutsidePress } from "../../hooks/useOutsidePress";
import { DESIGN_ICONS } from "../../theme/designIcons";
import { AccountMenu } from "./AccountMenu";
import { AlertsRailButton } from "./AlertsRailButton";
import { NavFlyout } from "./NavFlyout";
import { RailButton } from "./RailButton";
import type { NavLink, NavSectionKey } from "./navSections";
import { useFlyoutHover } from "./useFlyoutHover";
import type { NavModel } from "./useNavModel";
import styles from "./NavRail.module.css";

/** Rail width; AppLayout reserves it for AppShell's navbar slot. */
export const NAV_RAIL_WIDTH = 72;

const FLYOUT_ID = "nav-section-flyout";

interface NavRailProps {
  nav: NavModel;
  /** Onboarding wizard: logo and utilities only, no section buttons. */
  hideNavLinks?: boolean;
}

/**
 * Desktop navigation (design handoff §11): a 72px rail of section buttons.
 * Hovering a section opens its flyout beside the rail; clicking one keeps it
 * open until a page is picked, the user presses outside or Escape. The foot
 * holds Onboarding (until it's complete), Alerts, Chat and the staff avatar,
 * whose menu holds everything else.
 *
 * Rail and flyout share one wrapper, so moving the pointer between them never
 * leaves it (the hover bridge); leaving the wrapper starts the close grace.
 */
export const NavRail: React.FC<NavRailProps> = ({
  nav,
  hideNavLinks = false,
}) => {
  const flyout = useFlyoutHover<NavSectionKey>();
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  useOutsidePress(!!flyout.openKey, flyout.close, [wrapperRef]);
  // Keep rendering the last section while the panel fades out.
  const [shownKey, setShownKey] = React.useState<NavSectionKey | null>(null);
  if (flyout.openKey && flyout.openKey !== shownKey) {
    setShownKey(flyout.openKey);
  }
  const shownSection =
    nav.sections.find((section) => section.key === shownKey) ?? null;

  const go = (path: string) => {
    flyout.close();
    nav.navigate(path);
  };
  const selectLink = (link: NavLink) => go(link.path);

  return (
    <Box
      ref={wrapperRef}
      className={styles.wrapper}
      onMouseEnter={flyout.cancel}
      onMouseLeave={flyout.closeSoon}
      onKeyDown={(event) => {
        if (event.key === "Escape") flyout.close();
      }}
    >
      <Box component="nav" aria-label="Main" className={styles.rail}>
        <Logo
          variant="icon"
          width={38}
          height={38}
          className={styles.logo}
          alt="Pagoda"
        />

        <Stack gap={8} align="center" className={styles.sections}>
          {!hideNavLinks &&
            nav.sections.map((section) => (
              <RailButton
                key={section.key}
                icon={section.icon}
                label={section.label}
                active={nav.activeSection?.key === section.key}
                lit={flyout.openKey === section.key}
                aria-expanded={flyout.openKey === section.key}
                aria-controls={FLYOUT_ID}
                onMouseEnter={() => flyout.hoverOpen(section.key)}
                onFocus={() => flyout.hoverOpen(section.key)}
                onClick={() => flyout.pin(section.key)}
              />
            ))}
        </Stack>

        <Stack gap={2} align="center" className={styles.utilities}>
          {/* Owners only, until every onboarding card is done. */}
          {nav.canAccessOnboarding && (
            <RailButton
              icon={DESIGN_ICONS.Rocket}
              label="Onboarding"
              active={nav.pathname === "/onboarding"}
              onMouseEnter={flyout.closeSoon}
              onClick={() => go("/onboarding")}
            />
          )}
          <AlertsRailButton onMouseEnter={flyout.closeSoon} />
          {nav.showChat && (
            <RailButton
              icon={DESIGN_ICONS.MessageCircle}
              label="Chat"
              badge={nav.chatUnreadCount}
              active={nav.pathname.startsWith("/admin/chat")}
              onMouseEnter={flyout.closeSoon}
              onClick={() => go("/admin/chat")}
            />
          )}
          {/* TODO(nav): Help (design handoff §12) — a menu with "Ask a
              question" (support chat) and "Report a bug" (two-page modal).
              Hidden until both flows exist:
          <RailButton icon={DESIGN_ICONS.CircleHelp} label="Help" ... /> */}
          <Box onMouseEnter={flyout.close} className={styles.account}>
            <AccountMenu
              account={nav.account}
              showLocationSwitcher={nav.showLocationSwitcher}
              availableLocations={nav.availableLocations}
              selectedSalonId={nav.selectedSalonId}
              handleSignOut={nav.handleSignOut}
              onNavigate={go}
            />
          </Box>
        </Stack>
      </Box>

      <NavFlyout
        id={FLYOUT_ID}
        section={shownSection}
        open={!!flyout.openKey}
        pathname={nav.pathname}
        onSelect={selectLink}
      />
    </Box>
  );
};
